import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Input, useDisclosure, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { MenuAction, RanaButton, RanaModal, RanaTable } from "@nens/ranalib";
import { Template } from "@nens/threedi/openapi-client";

import { HeaderBox } from "@/components/header-box";
import { useHccSimulationTemplatesQuery } from "@/queries/query-hcc-simulation-templates";

import {
  useHccDeleteSimulationTemplateMutation,
  useHccRenameSimulationTemplateMutation,
} from "../api/hcc-simulation-templates";
import { useModelSchematisationPermission } from "../hooks/use-model-schematisation-permission";
import { useSchematisationDetailsContext } from "../schematisation-details-context";

// from HCC apps

export function SchematisationTemplatesSection() {
  return (
    <AnimateSize>
      <SchematisationTemplatesSectionContent />
    </AnimateSize>
  );
}

function SchematisationTemplatesSectionContent() {
  const { schematisation, threediModel } = useSchematisationDetailsContext();

  const intl = useIntl();
  const toast = useToast();
  const { canEditModelSchema } = useModelSchematisationPermission(schematisation.id);

  const { data: templatesResponse, isLoading: isTemplatesLoading } = useHccSimulationTemplatesQuery(
    threediModel?.id
  );

  const { mutate: deleteTemplate } = useHccDeleteSimulationTemplateMutation(threediModel?.id);
  const { mutate: renameTemplate } = useHccRenameSimulationTemplateMutation(threediModel?.id);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [renameTemplateId, setRenameTemplateId] = useState(0);
  const [renameTemplateName, setRenameTemplateName] = useState("");

  if (!templatesResponse || templatesResponse.results.length === 0) {
    return null;
  }

  const openRenameModal = (template: Template) => {
    setRenameTemplateId(template.id ?? 0);
    setRenameTemplateName(template.name);
    onOpen();
  };

  const handleDelete = (template: Template) => {
    if (!template.id) return;
    deleteTemplate(template.id, {
      onSuccess: () =>
        toast({
          status: "success",
          title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
          description: intl.formatMessage({
            id: "file.schematisation.template_deleted",
            defaultMessage: "Simulation template deleted",
          }),
        }),
      onError: () =>
        toast({
          status: "error",
          title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
          description: intl.formatMessage({
            id: "file.schematisation.template_delete_error",
            defaultMessage: "Failed to delete simulation template",
          }),
        }),
    });
  };

  const handleRename = () => {
    renameTemplate(
      { id: renameTemplateId, name: renameTemplateName },
      {
        onSuccess: () => {
          toast({
            status: "success",
            title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
            description: intl.formatMessage({
              id: "file.schematisation.template_renamed",
              defaultMessage: "Simulation template renamed",
            }),
          });
          onClose();
        },
        onError: () =>
          toast({
            status: "error",
            title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
            description: intl.formatMessage({
              id: "file.schematisation.template_rename_error",
              defaultMessage: "Failed to rename simulation template",
            }),
          }),
      }
    );
  };

  const getActions = (template: Template): MenuAction[] => {
    return [
      {
        label: intl.formatMessage({
          id: "file.schematisation.template_rename",
          defaultMessage: "Rename template",
        }),
        icon: <EditIcon />,
        action: () => openRenameModal(template),
      },
      {
        label: intl.formatMessage({
          id: "file.schematisation.template_delete",
          defaultMessage: "Delete template",
        }),
        icon: <DeleteIcon />,
        action: () => handleDelete(template),
      },
    ];
  };

  return (
    <>
      <HeaderBox
        boxHeight="fit"
        name={
          <FormattedMessage
            id="file.schematisation.simulation_templates"
            defaultMessage="Simulation templates"
          />
        }
      >
        {/*
         * We use RanaTable instead of DetailTable here because we need column headers
         * ("Name" and "From simulation") to distinguish the two data columns. DetailTable
         * is a label-value layout without header support.
         */}
        <RanaTable
          columnDefinitions={[
            {
              width: "table_cell",
              title: intl.formatMessage({
                id: "file.schematisation.template_name",
                defaultMessage: "Name",
              }),
            },
            {
              width: "table_cell",
              title: intl.formatMessage({
                id: "file.schematisation.template_from_simulation",
                defaultMessage: "From simulation",
              }),
            },
          ]}
          tableDataIds={templatesResponse?.results?.map((t) => String(t.id)) ?? []}
          hasRowActions={canEditModelSchema}
          isLoading={isTemplatesLoading}
          emptyElement={
            <RanaTable.Empty>
              <FormattedMessage
                id="file.schematisation.no_simulation_templates"
                defaultMessage="No simulation templates found."
              />
            </RanaTable.Empty>
          }
          sx={{
            "& td:nth-of-type(2), & th:nth-of-type(2)": {
              borderLeftWidth: "base",
              borderColor: "brand.border",
            },
          }}
        >
          <RanaTable.Container borderWidth={0} minHeight="auto" height="auto">
            <RanaTable.Header />
            <RanaTable.Body>
              {templatesResponse?.results?.map((template) => (
                <RanaTable.Row
                  rowActions={canEditModelSchema ? getActions(template) : undefined}
                  key={String(template.id)}
                  rowId={String(template.id)}
                >
                  <RanaTable.Cell>{template.name}</RanaTable.Cell>
                  <RanaTable.Cell>
                    {template.simulation?.name ??
                      intl.formatMessage({
                        id: "error.not_available",
                        defaultMessage: "Not available",
                      })}
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
      </HeaderBox>
      {canEditModelSchema && isOpen && (
        <RanaModal
          isOpen={isOpen}
          onClose={onClose}
          topBar={intl.formatMessage({
            id: "file.schematisation.template_rename_modal_title",
            defaultMessage: "Rename template",
          })}
          buttons={[
            <RanaButton variant="primary" key="save" onClick={handleRename}>
              {intl.formatMessage({ id: "action.save", defaultMessage: "Save" })}
            </RanaButton>,
          ]}
        >
          <Input
            size="sm"
            value={renameTemplateName}
            onChange={(e) => setRenameTemplateName(e.target.value)}
          />
        </RanaModal>
      )}
    </>
  );
}
